<template>
  <div class="container-fluid mt-6 py-4">
    <div class="row px-8">
      <div class="col-12">
        <!-- Progress Stepper -->
        <div class="stepper d-flex align-items-center mb-3">
          <template v-for="(step, index) in steps" :key="index">
            <div
              class="step d-flex align-items-center"
              :class="{
                active: currentStep === index + 1,
                completed: currentStep > index + 1,
              }"
            >
              <div class="step-circle d-flex align-items-center">
                <span class="step-number">{{ index + 1 }}</span>
                <span class="step-label">{{ step.title }}</span>
              </div>
            </div>
            <div v-if="index < steps.length - 1" class="step-separator">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </div>
          </template>
        </div>

        <!-- Header -->
        <div class="d-flex align-items-center mb-3">
          <h3 class="mb-0 me-2">Transfer</h3>
          <span class="text-sm text-secondary"
            >• Streamline your local domestic payouts with bulk transfer
            capabilities</span
          >
        </div>

        <!-- Main Content Card -->
        <div class="card main-content-card">
          <div class="card-body p-4">
            <UploadTemplateStep
              v-if="currentStep === 1"
              :is-processing="isProcessing"
              @file-selected="handleFileSelected"
              @remove-file="handleRemoveFile"
              @upload-success="handleUploadSuccess"
              @upload-failed="handleProcessingFailed"
              @update:isProcessing="isProcessing = $event"
            />
            <PreviewEntriesStep
              v-if="currentStep === 2"
              :batchUUID="currentBatchUUID"
              :fileName="selectedFile ? selectedFile.name : ''"
              :is-existing-batch="isExistingBatch"
              @update:isProcessing="isProcessing = $event"
              @processing-failed="handleProcessingFailed"
              @back-to-upload="handleBackToUpload"
              @proceed-to-review="handleProceedToReview"
            />
            <ReviewTransferStep
              v-if="currentStep === 3"
              :batchUUID="currentBatchUUID"
              :transferData="reviewData"
              :fileName="uploadedFileName"
              :is-existing-batch="isExistingBatch"
              @summary-updated="handleSummaryUpdated"
              @balance-updated="handleBalanceUpdated"
              @confirm="proceedToPayment"
              @back="goToPreviewEntries"
            />
            <PaymentStep
              v-if="currentStep === 4"
              :batchSummary="currentBatchSummary"
              :availableBalance="merchantBalance"
              :batchUUID="currentBatchUUID"
              @submit-payment="handlePaymentSubmission"
              @back="goToReview"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Processing Modal -->
    <div
      v-if="isProcessing"
      class="modal fade show"
      style="display: block; background-color: rgba(0, 0, 0, 0.5)"
      aria-modal="true"
      role="dialog"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div
          class="modal-content border-0"
          style="box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.175) !important"
        >
          <div class="modal-body text-center p-4">
            <img
              src="/assets/loading-swift.gif"
              alt="Processing..."
              width="80"
              class="mb-3"
            />
            <h5 class="modal-title mb-2 fw-bold">Please wait..</h5>
            <p class="text-sm text-secondary mb-0">
              Your file is being uploaded. This may take a moment. Please
              <strong>do not refresh or close</strong> the page until the
              process is complete.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";

// Import Step Components
import UploadTemplateStep from "./components/UploadTemplateStep.vue";
import PreviewEntriesStep from "./components/PreviewEntriesStep.vue";
import ReviewTransferStep from "./components/ReviewTransferStep.vue";
import PaymentStep from "./components/PaymentStep.vue";
import Swal from "sweetalert2";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { getBatchSummary } from "@/api/transfer"; // Import getBatchSummary API

const store = useStore();
store.state.hideConfigButton = false;
store.state.showTransferNavbar = true;
store.state.showNavbar = false;
store.state.showSidenav = false;
store.state.showFooter = false;

// Stepper State
const steps = ref([
  { title: "Upload Template" },
  { title: "Preview Entries" },
  { title: "Review Transfer" },
  { title: "Payment" },
]);

const route = useRoute();
const queryCurrentStep = Number(route.query.currentStep);

const currentStep = ref(isNaN(queryCurrentStep) ? 1 : queryCurrentStep);
const currentBatchUUID = ref(null);
const reviewData = ref([]);
const uploadedFileName = ref(""); // Keep this one
const merchantBalance = ref(0);
const currentBatchSummary = ref(null);
const isExistingBatch = ref(false); // Track if this is existing batch vs new upload

// Shared State
const selectedFile = ref(null);
const isProcessing = ref(false); // For the global modal

// Handle external navigation with batchId (e.g., from change payment method)
onMounted(async () => {
  const batchIdFromQuery = route.query.batchId;
  
  // If navigating to preview entries step with batchId
  if (currentStep.value === 2 && batchIdFromQuery) {
    currentBatchUUID.value = batchIdFromQuery;
    isExistingBatch.value = true; // This is existing batch from transaction list
    console.log('Transfer: Navigating to PreviewEntriesStep with existing batchId:', batchIdFromQuery);
  }
  // If navigating to payment step with batchId, fetch the summary
  else if (currentStep.value === 4 && batchIdFromQuery) {
    currentBatchUUID.value = batchIdFromQuery;
    
    try {
      const summaryResponse = await getBatchSummary(batchIdFromQuery);
      if (summaryResponse && summaryResponse.status === 'success' && summaryResponse.data) {
        currentBatchSummary.value = summaryResponse.data;
        console.log('Transfer: Batch summary loaded for external navigation:', summaryResponse.data);
      } else {
        console.error('Transfer: Failed to fetch batch summary for batchId:', batchIdFromQuery);
        Swal.fire(
          "Error", 
          "Failed to load transaction summary. Please try again.", 
          "error"
        );
        // Reset to step 1 if summary can't be loaded
        currentStep.value = 1;
        currentBatchUUID.value = null;
      }
    } catch (error) {
      console.error('Transfer: Error fetching batch summary:', error);
      Swal.fire(
        "Error", 
        "An error occurred while loading transaction summary.", 
        "error"
      );
      // Reset to step 1 if there's an error
      currentStep.value = 1;
      currentBatchUUID.value = null;
    }
  }
});

const handleFileSelected = (file) => {
  selectedFile.value = file; // Still useful for displaying filename, etc.
};

const handleUploadSuccess = (data) => {
  // data should be { uuid: '...', fileName: '...' }
  currentBatchUUID.value = data.uuid;
  isExistingBatch.value = false; // This is new upload
  // Store the actual uploaded file name for ReviewTransferStep
  uploadedFileName.value = data.fileName;
  if (!selectedFile.value || selectedFile.value.name !== data.fileName) {
    selectedFile.value = { name: data.fileName };
  }
  currentStep.value = 2; // Move to preview step (numeric)
  isProcessing.value = false;
};

const handleRemoveFile = async () => {
  selectedFile.value = null;
  currentBatchUUID.value = null; // Clear UUID
  currentStep.value = 1;
  isProcessing.value = false;
  reviewData.value = [];
  isExistingBatch.value = false; // Reset when file is removed
};

// goToPreviewStep is removed as navigation is handled by handleUploadSuccess

const handleProcessingFailed = (errorMessage) => {
  handleRemoveFile();
  Swal.fire(
    "Error",
    errorMessage ||
      "File processing failed. Please try again with a valid file.",
    "error"
  );
};

// --- Methods related to PreviewEntriesStep (New Event Handlers) ---
const handleBackToUpload = () => {
  handleRemoveFile(); // This already sets currentStep to 1 and clears file
};

const handleProceedToReview = async (dataFromPreview) => {
  reviewData.value = dataFromPreview;
  currentStep.value = 3; // Use 3 for ReviewTransferStep
};

// --- Methods for ReviewTransferStep and PaymentStep (Placeholders) ---
// eslint-disable-next-line no-unused-vars
const handlePayment = () => {
  // Handle final payment submission logic
  Swal.fire("Info", "Transfer Submitted (Placeholder)!", "info");
  // Reset to step 1 or show success page
  currentStep.value = 1;
  handleRemoveFile(); // Clear data
};

const handleSummaryUpdated = (summaryData) => {
  currentBatchSummary.value = summaryData;
};

const handleBalanceUpdated = (balance) => {
  merchantBalance.value = balance;
};

const proceedToPayment = () => {
  if (currentBatchSummary.value) {
    currentStep.value = 4; // Use 4 for PaymentStep
  } else {
    console.warn("Batch summary not yet available to proceed to payment.");
  }
};

const goToPreviewEntries = () => {
  currentStep.value = 2; // Navigate back to PreviewEntriesStep
};

const goToReview = () => {
  currentStep.value = 3; // Navigate back to ReviewTransferStep
};

const handlePaymentSubmission = () => {
  console.log(
    "Payment submitted with summary:",
    currentBatchSummary.value,
    "and balance:",
    merchantBalance.value
  );
  // Actual payment submission logic here
  Swal.fire("Info", "Payment Processed (Placeholder)!", "info");
  currentStep.value = 1; // Reset to upload step
  handleRemoveFile(); // Clear data
};
</script>

<style scoped>
/* Global Card Styling (from original Transfer.vue) */
.main-content-card {
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border: none;
}

/* Stepper Styles (from original Transfer.vue) */
.stepper {
  padding: 0;
  margin-top: 20px;
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-start;
  width: fit-content;
}

.step {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  flex-grow: 0;
  flex-shrink: 0;
}

.step-circle {
  height: 32px;
  border-radius: 16px;
  background-color: #e9ecef;
  color: #8392ab;
  display: flex;
  align-items: center;
  padding: 0 12px 0 0;
  position: relative;
  z-index: 2;
  font-weight: 500;
  font-size: 13px;
  border: 1px solid #dee2e6;
  transition:
    background-color 0.3s ease,
    color 0.3s ease,
    border-color 0.3s ease;
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #8392ab;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  font-weight: 600;
  transition: background-color 0.3s ease;
  flex-shrink: 0;
}

.step.active .step-circle {
  background-color: #fff;
  color: #0d6efd;
  border-color: #0d6efd;
}
.step.active .step-number {
  background-color: #0d6efd;
  color: white;
}

.step.completed .step-circle {
  background-color: #e9ecef;
  color: #6c757d;
  border-color: #dee2e6;
}
.step.completed .step-number {
  background-color: #6c757d;
  color: white;
}

.step:not(.active):not(.completed) .step-circle {
  background-color: #e9ecef;
  color: #6c757d;
  border-color: #dee2e6;
}
.step:not(.active):not(.completed) .step-number {
  background-color: #6c757d;
  color: white;
}

.step-label {
  white-space: nowrap;
  margin-left: 4px;
}

.step-separator {
  display: flex;
  align-items: center;
  color: #adb5bd;
  font-size: 12px;
  flex-shrink: 0;
}
.step-separator svg {
  width: 16px;
  height: 16px;
}

/* Modal styles (if any specific to the modal itself, not its content) */
.modal.fade.show {
  /* Basic modal backdrop */
}
.modal-dialog-centered .modal-content {
  /* Styles for the modal box */
}
</style>
